import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@/store/root-state';
import type { IScheduledEmailContentV2 } from '@/types/email.types';
import { selectScheduledEmails } from '@/store/slices/scheduled-emails/selectors';
import { selectThemePacks } from '@/store/slices/templates/selectors';
import { resolveEmailCampaignsThemePack } from '@/utils/resolve-email-campaigns-theme-pack';
import { currentTemplateIdOf } from './helpers';
import type { CampaignEditorFields } from './types';

export const selectCampaignEditorState = (state: RootState) => state.campaignEditor;

export const selectCampaignEditorFields = (state: RootState) => state.campaignEditor.fields;
export const selectCampaignEditorField =
  <K extends keyof CampaignEditorFields>(key: K) =>
  (state: RootState) =>
    state.campaignEditor.fields[key];

export const selectCampaignEditorInitialized = (state: RootState) => state.campaignEditor.initialized;
export const selectCampaignEditorBlocks = (state: RootState) => state.campaignEditor.blocks;
export const selectSelectedBlockId = (state: RootState) => state.campaignEditor.selectedBlockId;
export const selectThemeColorPackId = (state: RootState) => state.campaignEditor.fields.themeColorPackId;
export const selectHideBrandliveLogo = (state: RootState) => state.campaignEditor.fields.hideBrandliveLogo;
export const selectBackgroundColor = (state: RootState) => state.campaignEditor.fields.backgroundColor;
export const selectContainerColor = (state: RootState) => state.campaignEditor.fields.containerColor;
export const selectCampaignEditorThemeMode = (state: RootState) => state.campaignEditor.fields.themeMode;

export const selectSelectedBlock = createSelector(
  [selectCampaignEditorBlocks, selectSelectedBlockId],
  (blocks, selectedBlockId) => blocks?.find((block) => block.id === selectedBlockId) ?? null
);

export const selectSortableBlockIds = createSelector(
  [selectCampaignEditorBlocks],
  (blocks) => blocks?.map((block) => block.id) ?? []
);

// unlike useGetEmailCampaignsThemePack, an email with no theme assigned has no theme here -
// the editor only falls back to the default pack for display
export const selectCampaignEditorTheme = createSelector(
  [selectThemePacks, selectThemeColorPackId],
  (themePacks, themeColorPackId) =>
    themeColorPackId !== undefined ? resolveEmailCampaignsThemePack(themePacks, themeColorPackId) : undefined
);

export const selectCampaignEditorEmail = createSelector(
  [selectScheduledEmails, (state: RootState) => state.campaignEditor.campaignUuid],
  (emails, campaignUuid) => (campaignUuid ? emails?.find((email) => email.uuid === campaignUuid) : undefined)
);

export const selectCampaignEditorContent = createSelector(
  [selectCampaignEditorEmail],
  (email) => email?.customizations.v2 as IScheduledEmailContentV2 | undefined
);

export const selectCampaignEditorCurrentTemplateId = createSelector([selectCampaignEditorEmail], (email) =>
  email ? currentTemplateIdOf(email) : undefined
);
